import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'react-hot-toast';
import { 
  ShoppingCart, Search, Settings, LogOut, Sparkles, 
  CheckCircle2, Globe, Package, Facebook, Instagram, Twitter, 
  Youtube, Mail, MessageCircle, User as UserIcon, Camera, Trash2, HeartOff 
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { collection, query, getDocs, orderBy, limit, doc, updateDoc, addDoc, deleteDoc, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings, useLanguage, useCart, useInstruments, useCurrency } from '../contexts/SiteContexts';
import { useNotifications } from '../contexts/NotificationContext';
import { cn, compressImageFile } from '../lib/utils';
import { LOGO_ICON, LOGO_FULL } from '../assets/logos';
import { Navbar } from './layout/Navbar';
import { BottomNav } from './layout/BottomNav';
import { AISupportChat } from './AISupportChat';
import { handleFirestoreError, OperationType } from '../firebase';
import { SuspenseLoader } from './utils/SuspenseLoader';
import { Storefront } from './store/Storefront';

const SignupPage = React.lazy(() => import('./SignupPage').then(m => ({ default: m.SignupPage })));
const OriginStore = React.lazy(() => import('./store/OriginStore').then(m => ({ default: m.OriginStore })));
const UserProfile = React.lazy(() => import('./UserProfile').then(m => ({ default: m.UserProfile })));
const IntelligenceHub = React.lazy(() => import('./IntelligenceHub').then(m => ({ default: m.IntelligenceHub })));
const AdminPanel = React.lazy(() => import('./admin/AdminPanel').then(m => ({ default: m.AdminPanel })));
const AdminLoginPage = React.lazy(() => import('./admin/AdminLoginPage').then(m => ({ default: m.AdminLoginPage })));
const AboutPage = React.lazy(() => import('./StaticPages').then(m => ({ default: m.AboutPage })));
const ShippingPage = React.lazy(() => import('./StaticPages').then(m => ({ default: m.ShippingPage })));
const ReturnsPage = React.lazy(() => import('./StaticPages').then(m => ({ default: m.ReturnsPage })));
const PrivacyPage = React.lazy(() => import('./StaticPages').then(m => ({ default: m.PrivacyPage })));
const BlogListPage = React.lazy(() => import('./BlogPages').then(m => ({ default: m.BlogListPage })));
const BlogDetailPage = React.lazy(() => import('./BlogPages').then(m => ({ default: m.BlogDetailPage })));
const TrackingDashboard = React.lazy(() => import('./tracking/TrackingDashboard').then(m => ({ default: m.TrackingDashboard })));
const ShipmentTrackingDashboard = React.lazy(() => import('./tracking/ShipmentTrackingDashboard').then(m => ({ default: m.ShipmentTrackingDashboard })));
const DoctorHub = React.lazy(() => import('./doctorHub/DoctorHub').then(m => ({ default: m.DoctorHub })));
const LegalDocumentVault = React.lazy(() => import('./legalVault/LegalDocumentVault').then(m => ({ default: m.LegalDocumentVault })));
const ProductCatalogManager = React.lazy(() => import('./catalog/ProductCatalogManager').then(m => ({ default: m.ProductCatalogManager })));
const CategoriesPage = React.lazy(() => import('./store/CategoriesPage').then(m => ({ default: m.CategoriesPage })));
const NeedsPage = React.lazy(() => import('./needs/NeedsPage').then(m => ({ default: m.NeedsPage })));
const DownloadPage = React.lazy(() => import('./pages/DownloadPage').then(m => ({ default: m.DownloadPage })));

// Secondary Page Views
const WishlistPage = () => <ProductCatalogManager />;
const SearchPage = () => <ProductCatalogManager />;

export const MainContent = () => {
  const { t, language } = useLanguage();
  const { user, profile } = useAuth();
  const { settings } = useSiteSettings();
  const { cart, setIsCartOpen } = useCart();
  const { notifications } = useNotifications();
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<any[]>([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'blogs'), orderBy('createdAt', 'desc'), limit(10)));
        setBlogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error: any) {
        console.warn("Blogs fetch error:", error);
      }
    };
    fetchBlogs();
  }, []);

  const location = useLocation();

  // Check for profile completeness after login
  useEffect(() => {
    if (user && profile && (!profile.phone || !profile.address)) {
      setCompleteProfileOpen(true);
    }
  }, [user, profile]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    const form = e.target as HTMLFormElement;
    const phone = (form.elements.namedItem('phone') as HTMLInputElement).value.trim();
    const address = (form.elements.namedItem('address') as HTMLTextAreaElement).value.trim();

    if (!phone || !address) {
      toast.error('Please fill in both phone and address.');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        phone,
        address,
        profileComplete: true,
        updatedAt: new Date().toISOString()
      });
      setCompleteProfileOpen(false);
      toast.success('Profile updated successfully!');
    } catch (err: any) {
      console.error("Profile update error:", err);
      handleFirestoreError(err, OperationType.UPDATE, `users/${user.uid}`);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  const handleLogoTap = () => {
    setTapCount(prev => {
      const next = prev + 1;
      if (next >= 10) {
        navigate('/admin/login');
        return 0;
      }
      return next;
    });
  };

  // SEO & AEO Automation - Dynamic Head Updates
  useEffect(() => {
    if (settings) {
      document.title = (settings as any).seoTitle || 'Hamim Enterprises | Surgical Instruments';
      
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', (settings as any).seoDescription || '');
      
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', (settings as any).seoKeywords || '');
    }
  }, [settings]);

  // AEO (AI Engine Optimization) - JSON-LD Structured Data
  useEffect(() => {
    const existingScript = document.getElementById('aeo-schema');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'aeo-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Hamim Enterprises",
      "alternateName": "Hamim Enterprise Sialkot",
      "description": (settings as any).seoDescription || "Leading manufacturer of precision surgical instruments in Sialkot, Pakistan.",
      "url": window.location.origin,
      "logo": settings.logoUrl,
      "image": settings.splashLogoUrl,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Industrial Estate",
        "addressLocality": "Sialkot",
        "addressRegion": "Punjab",
        "postalCode": "51310",
        "addressCountry": "PK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "32.4945",
        "longitude": "74.5229"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": settings.whatsappNumber,
        "contactType": "sales",
        "areaServed": "Worldwide",
        "availableLanguage": ["English", "Urdu", "Arabic"]
      },
      "knowsAbout": ["Surgical Instruments", "Orthopedic Implants", "Medical Device Manufacturing", "Urology Tools"],
      "brand": {
        "@type": "Brand",
        "name": "Hamim"
      }
    });
    document.head.appendChild(script);
  }, [settings]);
  
  const isApp = Capacitor.isNativePlatform();

  return (
    <div 
      dir={language === 'ur' ? 'rtl' : 'ltr'} 
      className={cn(
        "min-h-screen selection:bg-indigo-100 selection:text-indigo-900 font-sans transition-colors duration-500",
        "bg-white text-black",
        language === 'ur' && "font-urdu"
      )}
    >
        {isApp ? (
          <div className="flex flex-col min-h-screen">
             <header className="sticky top-0 z-[100] bg-white/95 backdrop-blur-md px-5 h-20 flex items-center justify-between border-b border-slate-100 rounded-b-[2.5rem] shadow-sm">
                <div className="flex items-center space-x-3" onClick={handleLogoTap}>
                   <Link to="/" className="w-11 h-11 rounded-2xl bg-white border border-slate-100 flex items-center justify-center p-2.5 shadow-xl shadow-slate-100/50 group active:scale-95 transition-all">
                    <img src={settings.logoIconUrl || LOGO_ICON} alt="" className="w-full h-full object-contain" />
                   </Link>
                   <div className="flex flex-col">
                    <span className="font-black tracking-tighter text-[10px] uppercase text-slate-400 leading-none mb-1">Elite Surgical Store</span>
                    <span className="font-black tracking-tighter text-base uppercase text-slate-900 leading-none">HAMIM <span className="text-red-600">ENTERPRISES</span></span>
                   </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setIsCartOpen(true)}
                    className="relative p-2.5 bg-slate-100 text-slate-600 rounded-2xl"
                  >
                    <ShoppingCart size={20} />
                    {cart.reduce((acc, item) => acc + item.quantity, 0) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full border border-white">
                        {cart.reduce((acc, item) => acc + item.quantity, 0)}
                      </span>
                    )}
                  </button>
                  <Link to="/search" className="p-2.5 bg-slate-100 text-slate-600 rounded-2xl"><Search size={20} /></Link>
                  <Link to={user ? "/profile" : "/signup"} className="p-0.5 bg-white border-2 border-slate-50 rounded-2xl shadow-sm">
                    <div className="w-10 h-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                      {user ? (
                        <img src={user.photoURL || undefined} alt="" className="w-full h-full rounded-2xl object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <UserIcon size={20} />
                      )}
                    </div>
                  </Link>
                </div>
             </header>
             
              <main className="flex-1 pb-28">
               <React.Suspense fallback={<SuspenseLoader />}>
                <Routes>
                  <Route path="/" element={<Storefront />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/catalog" element={<ProductCatalogManager />} />
                  <Route path="/categories" element={<CategoriesPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/download" element={<DownloadPage />} />
                  <Route path="/needs" element={<NeedsPage />} />
                  <Route path="/products/:origin" element={<OriginStore />} />
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/blogs" element={<BlogListPage blogs={blogs} />} />
                  <Route path="/blog/:id" element={<BlogDetailPage blogs={blogs} />} />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/shipping" element={<ShippingPage />} />
                  <Route path="/returns" element={<ReturnsPage />} />
                  <Route path="/tracking" element={<TrackingDashboard />} />
                  <Route path="/tracking/:trackingId" element={<TrackingDashboard />} />
                  <Route path="/shipment-tracking" element={<ShipmentTrackingDashboard />} />
                  <Route path="/shipment-dashboard" element={<ShipmentTrackingDashboard />} />
                  <Route path="/doctor-hub" element={<DoctorHub />} />
                  <Route path="/dr-to-dr" element={<DoctorHub />} />
                  <Route path="/legal-vault" element={<LegalDocumentVault />} />
                  <Route path="/compliance" element={<LegalDocumentVault />} />
                  <Route path="/documents" element={<LegalDocumentVault />} />
                  <Route path="/intelligence" element={<IntelligenceHub />} />
                  <Route path="*" element={<Storefront />} />
                </Routes>
               </React.Suspense>
             </main>
             
             <BottomNav />
             <AISupportChat />
          </div>
        ) : (
          <>
            <Navbar />
            <main className="flex-1 min-h-[60vh]">
             <React.Suspense fallback={<SuspenseLoader />}>
              <Routes>
                <Route path="/" element={<Storefront />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/catalog" element={<ProductCatalogManager />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/wishlist" element={<WishlistPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/download" element={<DownloadPage />} />
                <Route path="/needs" element={<NeedsPage />} />
                <Route path="/products/:origin" element={<OriginStore />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/blogs" element={<BlogListPage blogs={blogs} />} />
                <Route path="/blog/:id" element={<BlogDetailPage blogs={blogs} />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/returns" element={<ReturnsPage />} />
                <Route path="/tracking" element={<TrackingDashboard />} />
                <Route path="/tracking/:trackingId" element={<TrackingDashboard />} />
                <Route path="/shipment-tracking" element={<ShipmentTrackingDashboard />} />
                <Route path="/shipment-dashboard" element={<ShipmentTrackingDashboard />} />
                <Route path="/doctor-hub" element={<DoctorHub />} />
                <Route path="/dr-to-dr" element={<DoctorHub />} />
                <Route path="/legal-vault" element={<LegalDocumentVault />} />
                <Route path="/compliance" element={<LegalDocumentVault />} />
                <Route path="/documents" element={<LegalDocumentVault />} />
                <Route path="/intelligence" element={<IntelligenceHub />} />
                <Route path="*" element={<Storefront />} />
              </Routes>
             </React.Suspense>
            </main>
            <AISupportChat />
            <footer className="bg-slate-950 text-white py-24 mt-20">
              <div className="max-w-7xl mx-auto px-6 text-center md:text-left">
                {/* Global Compliance & Trust Signals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 border-b border-white/10 pb-16">
                  <Link to="/legal-vault" className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 hover:border-indigo-500/50 transition-all group">
                    <CheckCircle2 size={40} className="text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
                    <span className="font-black tracking-widest uppercase text-xs text-white">ISO 13485</span>
                    <span className="text-[11px] text-slate-400 font-medium group-hover:text-indigo-300 transition-colors">Certified Quality • View Vault</span>
                  </Link>
                  <Link to="/legal-vault" className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 hover:border-emerald-500/50 transition-all group">
                    <Globe size={40} className="text-emerald-400 mb-3 group-hover:scale-110 transition-transform" />
                    <span className="font-black tracking-widest uppercase text-xs text-white">CE Mark</span>
                    <span className="text-[11px] text-slate-400 font-medium group-hover:text-emerald-300 transition-colors">EU Conformity • View Vault</span>
                  </Link>
                  <Link to="/legal-vault" className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 hover:border-indigo-500/50 transition-all group">
                    <CheckCircle2 size={40} className="text-indigo-400 mb-3 group-hover:scale-110 transition-transform" />
                    <span className="font-black tracking-widest uppercase text-xs text-white">FDA Approved</span>
                    <span className="text-[11px] text-slate-400 font-medium group-hover:text-indigo-300 transition-colors">FEI Registration • View Vault</span>
                  </Link>
                  <Link to="/legal-vault" className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 hover:border-amber-500/50 transition-all group">
                    <Package size={40} className="text-amber-400 mb-3 group-hover:scale-110 transition-transform" />
                    <span className="font-black tracking-widest uppercase text-xs text-white">WHO GMP</span>
                    <span className="text-[11px] text-slate-400 font-medium group-hover:text-amber-300 transition-colors">Cleanroom Standard • View Vault</span>
                  </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-16 mb-20 items-center md:items-start">
                  <div className="col-span-1 md:col-span-2 space-y-6">
                    <div className="flex items-center justify-center md:justify-start space-x-4">
                      <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center p-3 shadow-2xl border border-white/10 overflow-hidden">
                        <img src={settings.logoIconUrl || LOGO_ICON} alt="" className="w-full h-full object-contain brightness-0 invert" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-3xl font-black tracking-tighter uppercase leading-none">HAMIM</span>
                        <span className="text-red-500 font-black tracking-[0.3em] text-xs uppercase underline underline-offset-4">ENTERPRISE</span>
                      </div>
                    </div>
                    <p className="text-slate-400 max-w-sm mx-auto md:mx-0 leading-relaxed text-sm font-medium">
                      The industry standard in high-precision surgical and industrial instrumentation. Powered by innovation, delivered with excellence.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-4">
                      <Link to="/blogs" className="px-6 py-2 bg-indigo-600/10 text-indigo-500 rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-lg shadow-indigo-900/10">
                        AI Insights Portal
                      </Link>
                      <Link to="/doctor-hub" className="px-6 py-2 bg-white/10 text-slate-300 rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all">
                        Doctor to Doctor Hub
                      </Link>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-10">Navigation</h4>
                    <ul className="space-y-5 text-slate-400 font-bold uppercase tracking-widest text-[11px]">
                      <li><Link to="/" className="hover:text-indigo-500 hover:ml-2 transition-all">{t('store')}</Link></li>
                      <li><Link to="/tracking" className="hover:text-indigo-500 hover:ml-2 transition-all">Track Shipment</Link></li>
                      <li><Link to="/doctor-hub" className="hover:text-indigo-500 hover:ml-2 transition-all text-indigo-400">Doctor to Doctor Hub</Link></li>
                      <li><Link to="/categories" className="hover:text-indigo-500 hover:ml-2 transition-all">{t('categories')}</Link></li>
                      <li><Link to="/shipping" className="hover:text-indigo-500 hover:ml-2 transition-all">Shipping & Logistics</Link></li>
                      <li><Link to="/about" className="hover:text-indigo-500 hover:ml-2 transition-all">{t('about')}</Link></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-10">Updates</h4>
                    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); toast.success('Subscribed!'); }}>
                      <input type="email" placeholder="Join the inner circle..." className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-600" required />
                      <button type="submit" className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20">{t('subscribe')}</button>
                    </form>
                  </div>
                </div>
                <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0">
                  <div className="flex flex-col items-center md:items-start space-y-2">
                    <p className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-500">© 2026 Hamim Elite Group • All Rights Reserved</p>
                    <p className="text-[11px] text-slate-600 font-bold uppercase tracking-widest">Global Logistics & Precision Instruments</p>
                  </div>
                  
                  {/* Social links */}
                  <div className="flex items-center space-x-4">
                    {settings.socialLinks?.facebook && (
                      <a href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-blue-600 transition-all group shadow-lg hover:shadow-blue-500/20">
                        <Facebook size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.socialLinks?.instagram && (
                      <a href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-600 transition-all group shadow-lg hover:shadow-pink-500/20">
                        <Instagram size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.socialLinks?.tiktok && (
                      <a href={settings.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-black transition-all group shadow-lg">
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-slate-400 group-hover:fill-white"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.06-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.03 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-3.49 3.03-6.41 6.32-6.57.14-.02.28-.01.41-.02V9.33c-.07 0-.15-.01-.23-.01-1.39-.01-2.91.43-3.86 1.43-.88.92-1.28 2.22-1.2 3.49.03 1.01.35 2.01 1 2.82.93 1.25 2.5 1.93 4.02 1.84a4.136 4.136 0 0 0 3.32-1.92c.66-.99.94-2.18.94-3.36V0h.01z"/></svg>
                      </a>
                    )}
                    {settings.socialLinks?.twitter && (
                      <a href={settings.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-slate-900 transition-all group shadow-lg">
                        <Twitter size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.socialLinks?.youtube && (
                      <a href={settings.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-red-600 transition-all group shadow-lg hover:shadow-red-500/20">
                        <Youtube size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.socialLinks?.google && (
                      <a href={settings.socialLinks.google} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-indigo-600 transition-all group shadow-lg hover:shadow-indigo-500/20">
                        <Mail size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.socialLinks?.website && (
                      <a href={settings.socialLinks.website} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-emerald-600 transition-all group shadow-lg">
                        <Globe size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                    {settings.whatsappNumber && (
                      <a href={`https://wa.me/${settings.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-green-600 transition-all group shadow-lg hover:shadow-green-500/20">
                        <MessageCircle size={18} className="text-slate-400 group-hover:text-white" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </footer>
          </>
        )}
        <AnimatePresence>
          {completeProfileOpen && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setCompleteProfileOpen(false)} />
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative w-full max-w-md bg-white rounded-[2rem] p-10 shadow-2xl"
              >
                <div className="flex flex-col items-center text-center mb-8">
                  <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mb-4">
                    <UserIcon size={40} />
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Complete Profile</h2>
                  <p className="text-slate-500 font-medium mt-2">Just a few more details to get started!</p>
                </div>

                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div>
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Phone Number</label>
                    <input 
                      name="phone" 
                      type="tel" 
                      required 
                      placeholder="+92..." 
                      className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Full Address</label>
                    <textarea 
                      name="address" 
                      required 
                      placeholder="Your hospital or business address..." 
                      className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold min-h-[100px]"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-indigo-100"
                  >
                    Save & Continue
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setCompleteProfileOpen(false)}
                    className="w-full py-3 text-slate-400 hover:text-slate-600 font-bold uppercase text-[11px] tracking-widest"
                  >
                    Later
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
    </div>
  );
};
